import React from 'react'
import {
	Controller,
	type Control,
	type FieldValues,
	type Path,
	type RegisterOptions,
	type UseFormSetValue,
	type UseFormTrigger,
	type UseFormClearErrors,
} from 'react-hook-form'

type RenderCTAArgs = {
	value: string
	hasError: boolean
	clear: () => void
}

type InputWithCTA_RHFProps<T extends FieldValues> =
	React.InputHTMLAttributes<HTMLInputElement> & {
		name: Path<T>
		control: Control<T>
		rules?: RegisterOptions<T, Path<T>>
		label?: string
		requiredMark?: boolean
		error?: string
		renderCTA?: (args: RenderCTAArgs) => React.ReactNode
		errorId?: string
		errorMinHeightPx?: number
		setValue?: UseFormSetValue<T>
		trigger?: UseFormTrigger<T>
		validateOnlyOnBlur?: boolean
		clearErrors?: UseFormClearErrors<T>
		normalizeOnBlur?: (value: string) => string
	}

export function InputBlurWithCTA_RHF<T extends FieldValues>({
	name,
	control,
	rules,
	label,
	requiredMark,
	error,
	renderCTA,
	className = '',
	errorId,
	id,
	setValue,
	trigger,
	validateOnlyOnBlur,
	clearErrors,
	errorMinHeightPx, // opzionale: se vuoi forzare via prop
	normalizeOnBlur,
	...inputProps
}: InputWithCTA_RHFProps<T>) {
	const reactId = React.useId()
	const inputId = id ?? `${String(name)}-${reactId}`
	const finalErrorId = errorId ?? `${String(name)}-error-${reactId}`
	// Provide a helpful placeholder when the field label is exactly "Email"
	const finalInputProps = {
		...inputProps,
		placeholder:
			inputProps.placeholder ??
			(label === 'Email'
				? 'Inserisci email'
				: label === 'Codice promozionale'
					? 'Inserisci codice promozionale'
					: label === 'Altro importo'
						? 'Inserisci importo'
						: label === 'IBAN'
							? 'Inserisci IBAN'
							: label === 'Numero conto corrente'
								? 'Inserisci numero conto corrente'
								: undefined),
	}

	return (
		<Controller
			name={name}
			control={control}
			rules={rules}
			render={({ field, fieldState }) => {
				const value = (field.value ?? '') as string
				const message = error ?? fieldState.error?.message
				const hasError = Boolean(message)
				const hasValue = typeof value === 'string' && value.trim().length > 0
				const isDisabled = !!inputProps.disabled

				return (
					<div className='form-control'>
						{/* LABEL */}
						{label && (
							<label htmlFor={inputId} className='field-label'>
								{label}
								{requiredMark && ' *'}
							</label>
						)}

						{/* ERROR (sempre presente) */}
						<div
							id={finalErrorId}
							role={hasError ? 'alert' : undefined}
							className={`error-message ${hasError ? '' : 'invisible'}`}
							// se non vuoi Tailwind: usa "is-invisible" invece di "invisible"
							style={
								errorMinHeightPx ? { minHeight: errorMinHeightPx } : undefined
							}
						>
							<svg
								className='error-icon'
								viewBox='0 0 20 20'
								fill='currentColor'
								aria-hidden='true'
							>
								<path
									fillRule='evenodd'
									d='M10 18a8 8 0 100-16 8 8 0 000 16zm-1-4h2v2H9v-2zm0-8h2v6H9V6z'
									clipRule='evenodd'
								/>
							</svg>

							<span className='error-text'>
								{hasError ? String(message) : 'placeholder'}
							</span>
						</div>

						{/* INPUT */}
						<div
							className='field'
							aria-invalid={hasError ? 'true' : 'false'}
							data-has-cta={renderCTA && hasValue ? 'true' : 'false'}
							data-readonly={inputProps.readOnly ? 'true' : 'false'}
							data-disabled={isDisabled ? 'true' : 'false'}
						>
							<input
								{...field}
								{...finalInputProps}
								id={inputId}
								value={value}
								onChange={(e) => {
									if (validateOnlyOnBlur && setValue) {
										setValue(name, e.target.value as any, {
											shouldDirty: true,
											shouldTouch: false,
											shouldValidate: false,
										})
										inputProps.onChange?.(e)
										return
									}

									field.onChange(e)
									inputProps.onChange?.(e)
								}}
								onBlur={async (e) => {
  const rawValue = e.target.value
  const normalizedValue = normalizeOnBlur
    ? normalizeOnBlur(rawValue)
    : rawValue

  if (normalizedValue !== rawValue) {
    if (setValue) {
      setValue(name, normalizedValue as any, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      })
    } else {
      field.onChange(normalizedValue)
    }
  }

  field.onBlur()
  inputProps.onBlur?.(e)

  if (validateOnlyOnBlur && trigger) {
    await trigger(name)
  }
}}
							/* 	onBlur={async (e) => {
									field.onBlur()
									inputProps.onBlur?.(e)

									if (validateOnlyOnBlur && trigger) {
										await trigger(name)
									}
								}} */
								aria-invalid={hasError ? 'true' : 'false'}
								aria-describedby={finalErrorId}
								className={`input-base ${className}`}
							/>

							{!inputProps.readOnly && !isDisabled && renderCTA && hasValue && (
						<div className='h-full shrink-0'>
									{renderCTA({
										value,
										hasError,
										clear: async () => {
											if (validateOnlyOnBlur && setValue) {
												setValue(name, '' as any, {
													shouldDirty: true,
													shouldTouch: true,
													shouldValidate: false,
												})

												clearErrors?.(name)

												 if (trigger) {
													await trigger(name)
												}  

												return
											}

											field.onChange('')
										},
									})}
								</div>
							)}
						</div>
					</div>
				)
			}}
		/>
	)
}
